import React, { createContext, useState, useContext, ReactNode } from 'react';
import endpoints from '../utils/commonVariables/endpoints';
import { viewModelEntities } from '../utils/commonVariables/entityKeys';
import api, { commonErrorMessage } from '../services/api';
import { NotificationsContext } from './NotificationsContext';
import { EntityTab } from '../types/entities/entities';
import { IHotelOwner } from '../types/entities/IHotelOwner';
import { IdHotelOwnerKey } from '../utils/userProfileContextHelpers';
import useTranslate from '../customHooks/useTranslate';

interface HotelOwnerContextType {
    ownerData: HotelOwnerState | null;
    getInitialOwnerData: () => Promise<IHotelOwner | undefined>;
    saveOwnerData: (data: HotelOwnerState) => void;
    updateOwnerData: (partialData: Partial<HotelOwnerState>) => void;
}

const HotelOwnerContext = createContext<HotelOwnerContextType | undefined>(
    undefined
);

export interface HotelOwnerState {
    id: string;
    signed: boolean;
    zinantisMembership: boolean;
    idCurrency: number;
    currency: string;
}

export const HotelOwnerProvider = ({ children }: { children: ReactNode }) => {
    const { setErrorMessage } = useContext(NotificationsContext);
    const translate = useTranslate();

    const [ownerData, setOwnerData] = useState<HotelOwnerState | null>(null);

    const getInitialOwnerData = async () => {
        const ownerContractModel: EntityTab<IHotelOwner> = {
            Id: true,
            Signed: true,
            ZinantisMembership: true,
            IdCurrency: true,
            Currency: {
                Name: true,
            },
            SystemUser: {
                UserName: true,
            },
            Users: {
                ReadOnly: true,
                Admin: true,
                SystemUser: {
                    UserName: true,
                },
            },
        };
        const IdHotelOwner = localStorage.getItem(IdHotelOwnerKey);
        const endpoint = endpoints.viewModel(
            viewModelEntities.HotelOwner,
            IdHotelOwner
        );
        try {
            const { data } = await api.post(endpoint, ownerContractModel);
            if (data?.Result) {
                return data?.Result as IHotelOwner;
            } else {
                setErrorMessage(translate(commonErrorMessage));
            }
        } catch (error) {
            setErrorMessage(translate(commonErrorMessage));
        }
    };

    const saveOwnerData = (data: HotelOwnerState) => {
        setOwnerData(data);
    };

    const updateOwnerData = (partialData: Partial<HotelOwnerState>) => {
        if (!ownerData) return;
        setOwnerData(prevData => {
            if (!prevData) return prevData;
            return { ...prevData, ...partialData } as HotelOwnerState;
        });
    };

    return (
        <HotelOwnerContext.Provider
            value={{
                ownerData,
                getInitialOwnerData,
                saveOwnerData,
                updateOwnerData,
            }}
        >
            {children}
        </HotelOwnerContext.Provider>
    );
};

export const useHotelOwner = () => {
    const context = useContext(HotelOwnerContext);
    if (!context) {
        throw new Error(
            'useHotelOwner must be used within a HotelOwnerProvider'
        );
    }
    return context;
};
